import type { Booking, BookingsHistoryTabs } from '@/scopes/account/bookings-overview/domain/types'
import { useBookingsOverviewStore } from '@/scopes/account/bookings-overview/infrastructure/useBookingsOverviewStore'
import { mqMin, tokPx } from '@sunwebgroup/ui-components'
import { Card, TabBar, Typography } from '@sunwebgroup/ui-components/uds'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'
import { getSortedCancelledBookings, getSortedPastBookings, isCancelledTabActive, isPastTabActive } from './selectors'

const BookingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(${tokPx('primitives_area_size_280')}, 1fr));
  gap: ${tokPx('primitives_space_16')};
  margin-top: ${tokPx('primitives_space_24')};

  @media ${mqMin('primitives_breakpoint_l_min')} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const EmptyMessage = styled(Typography)`
  margin-top: ${tokPx('primitives_space_24')};
`

function BookingItem({ booking }: { booking: Booking }) {
  return (
    <Card
      title={booking.accommodationName}
      subtitle={booking.destination}
      date={booking.period}
      mediaSrc={booking.imageSrc}
      href={`/mijn-sunweb/boeking?bookingNumber=${booking.bookingNumber}`}
    />
  )
}

function BookingsList({ bookings, emptyStateMessage }: { bookings: Booking[], emptyStateMessage: string }) {
  if (bookings.length === 0) {
    return (
      <EmptyMessage as="div" $variant="body-m-regular-snug">{emptyStateMessage}</EmptyMessage>
    )
  }

  return (
    <BookingsGrid>
      {bookings.map(booking => (
        <BookingItem key={booking.bookingNumber} booking={booking} />
      ))}
    </BookingsGrid>
  )
}

export function BookingsHistory() {
  const pastBookings = useBookingsOverviewStore(getSortedPastBookings)
  const cancelledBookings = useBookingsOverviewStore(getSortedCancelledBookings)

  const changeBookingHistoryTab = useBookingsOverviewStore(state => state.changeBookingHistoryTab)
  const activeTabs = {
    cancelled: useBookingsOverviewStore(isCancelledTabActive),
    past: useBookingsOverviewStore(isPastTabActive),
  }
  const intl = useIntl()

  if (pastBookings.length === 0 && cancelledBookings.length === 0)
    return

  return (
    <div>
      <TabBar onActiveChange={({ value }) => changeBookingHistoryTab(value as BookingsHistoryTabs)}>
        <TabBar.Tab value="past">
          {intl.formatMessage({ id: 'booking_overview_tab_past' })}
        </TabBar.Tab>
        <TabBar.Tab value="cancelled">
          {intl.formatMessage({ id: 'booking_overview_tab_cancelled' })}
        </TabBar.Tab>
      </TabBar>
      <>
        {activeTabs.past && <BookingsList bookings={pastBookings} emptyStateMessage={intl.formatMessage({ id: 'booking_overview_past_empty_state' })} />}
        {activeTabs.cancelled && <BookingsList bookings={cancelledBookings} emptyStateMessage={intl.formatMessage({ id: 'booking_overview_cancelled_empty_state' })} />}
      </>
    </div>
  )
}
